import type React from 'react';
import { createElement } from 'react';
import { MdDrafts } from 'react-icons/md';
import { AfIcon, BvIcon, DashboardIcon, OrdersIcon } from '@/assets/icons';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';

export interface NavItem {
	title: string;
	href?: string;
	submenu?: NavItem[];
	icon?: React.ReactNode;
	permissions?: string[];
}

export const navigationItems: NavItem[] = [
	{
		title: 'Home',
		href: '/',
		icon: createElement(DashboardIcon, { width: 20, height: 20 }),
		permissions: ['Dashboard:Visualize'],
	},
	{
		title: 'Benefits Verifications',
		href: '/benefits-verifications',
		icon: createElement(BvIcon, { width: 15, height: 15 }),
		permissions: ['BenefitVerification:Visualize'],
	},
	{
		title: 'Orders',
		icon: createElement(OrdersIcon, { width: 25, height: 25 }),
		submenu: [
			{
				title: 'Order Status',
				href: '/requests',
				permissions: ['Orders:Visualize'],
			},
			{
				title: 'Return Orders',
				href: '/returns',
				permissions: ['ReturnsConsignment:Visualize', 'Returns:Visualize'],
			},
			{
				title: 'Consignment Orders',
				href: '/consignments',
				permissions: ['OrderConsignmentManagment:Visualize'],
			},
		],
		permissions: [
			'OrderConsignmentManagment:Visualize',
			'ReturnsConsignment:Visualize',
			'Orders:Visualize',
			'Returns:Visualize',
		],
	},
	{
		title: 'Invoices',
		icon: createElement(AfIcon, { width: 20, height: 20 }),
		permissions: ['Invoices:Visualize'],
		submenu: [
			{ title: 'All Invoices', href: '/all-invoices' },
			{ title: 'Open Invoices', href: '/open-invoices' },
			{ title: 'Paid Invoices', href: '/paid-invoices' },
		],
	},
	{
		title: 'Payment Sessions',
		href: '/payment-locks',
		permissions: ['PaymentLock: Visualize'],
	},
	// { title: 'Denial Support', href: '/denial-support', permissions: ['DenialSupport:Visualize'] },
	{
		title: 'Accounting & Finance',
		icon: createElement(AfIcon, { width: 25, height: 25 }),
		permissions: ['Accounting:Visualize'],
		submenu: [
			{
				title: 'Invoices',
				icon: createElement(AfIcon, { width: 20, height: 20 }),
				permissions: ['InvoicesView:Visualize'],
				submenu: [
					{ title: 'All Invoices', href: '/all-invoices' },
					{ title: 'Open Invoices', href: '/open-invoices' },
					{ title: 'Paid Invoices', href: '/paid-invoices' },
				],
			},
			{
				title: 'Collections',
				href: '/collections',
				permissions: ['Collections:Visualize'],
			},
			{
				title: 'Denial Support',
				href: '/denial-support',
				permissions: ['DenialSupport:Visualize'],
			},
			{
				title: 'Billing Tools',
				icon: createElement(AfIcon, { width: 20, height: 20 }),
				permissions: ['BillingTools:Visualize'],
				submenu: [
					{ title: 'Misbilled HCPCS', href: '/misbilled-hcpcs' },
					{ title: 'Misbilled Units', href: '/misbilled-units' },
					{ title: 'Unmatched Claims To Invoices', href: '/unmatched-claims-to-invoices' },
					{ title: 'Unmatched Invoices To Claims', href: '/unmatched-invoices-to-claim' },
				],
			},
		],
	},
	// {
	// 	title: 'Patient Insights',
	// 	href: '/patient-insights',
	// 	icon: createElement(PatientInsightIcon, { width: 20, height: 20 }),
	// },
	// {
	// 	title: 'Chart Review',
	// 	href: '/chart-review',
	// 	icon: createElement(ChartReviewIcon, { width: 19, height: 19 }),
	// },
];

export const buildClinicKey = (clinic?: SelectItem | null) => {
	if (!clinic?.value) return '';
	return `${clinic.label}_${String(clinic.value).replaceAll('-', '')}`;
};

/**
 * Builds a documents navigation URL from a SharePoint URL, clinic ID, and clinic name.
 * Extracts folder path and file name from the SharePoint URL and constructs query parameters.
 *
 * @param url - The SharePoint URL containing the document path
 * @param clinicId - The clinic ID (GUID)
 * @param clinicName - The clinic name
 * @returns The documents URL with query parameters, or null if the URL cannot be built
 */
export const buildDocumentsUrl = (
	url: string,
	clinicId: string,
	clinicName: string,
): string | null => {
	if (!url || !clinicId || !clinicName) {
		return null;
	}

	const clinicKey = buildClinicKey({ label: clinicName, value: clinicId });
	const splitBy = `${clinicId}/`.replaceAll('-', '');
	let folderPath = '';

	if (url.split(splitBy).length > 1) {
		folderPath = url.split(splitBy).pop() || '';
	} else if (url.split(splitBy.toUpperCase()).length > 1) {
		folderPath = url.split(splitBy.toUpperCase()).pop() || '';
	}

	if (!folderPath) {
		return null;
	}

	// Remove trailing '/' if present
	folderPath = folderPath.replace(/\/$/, '');

	// Check if the path ends with a file (has an extension)
	const lastSegment = folderPath.split('/').pop() || '';
	const hasFileExtension =
		lastSegment.includes('.') && lastSegment.split('.').length > 1;

	let folderOnly = folderPath;
	let fileName = '';

	if (hasFileExtension) {
		// Extract file name and folder path
		const pathParts = folderPath.split('/');
		fileName = pathParts.pop() || '';
		folderOnly = pathParts.join('/');
	}

	// Build query params
	const params = new URLSearchParams();
	params.set('clinic', clinicKey);
	if (folderOnly) {
		params.set('folder', folderOnly);
	}
	if (fileName) {
		params.set('file', fileName);
	}

	return `/documents?${params.toString()}`;
};
